import { useCallback } from 'react';

import { MessagingPlatform } from '@this+that/models';
import log from 'loglevel';

import { HierarchicalFolder } from '../../models';
import { getUserLabels } from '../../services/labels.service';
import { getUserFolders } from '../../services/outlookFolders.service';
import { useIntegrationsStore, useLabelsStore } from '../../store';
import { useFetchCacheStore } from '../../store/FetchCache';
import { HierarchicalLabelOrFolder } from '../../store/Inbox/Labels/types';

const ensureEmailAddress = (folder: HierarchicalFolder): HierarchicalFolder => {
  return {
    ...folder,
    emailAddress: folder.emailAddress || '',
    children: folder.children.map(ensureEmailAddress),
  };
};

export const useFetchLabels = () => {
  const setInboxLabels = useLabelsStore((s) => s.setInboxLabels);
  const setLoadingFetchLabels = useLabelsStore((s) => s.setLoadingFetchLabels);

  const handleFetchLabels = useCallback(
    async (forceRefresh = false) => {
      const { isLabelsLoaded, markLabelsAsLoaded } =
        useFetchCacheStore.getState();

      if (!forceRefresh && isLabelsLoaded()) return;

      const { integrationAccounts } = useIntegrationsStore.getState();
      const hasGmailIntegration = integrationAccounts.some(
        (account) => account.platform === MessagingPlatform.GMAIL,
      );
      const hasOutlookIntegration = integrationAccounts.some(
        (account) => account.platform === MessagingPlatform.OUTLOOK,
      );

      if (!hasGmailIntegration && !hasOutlookIntegration) return;

      setLoadingFetchLabels(true);
      try {
        const results: HierarchicalLabelOrFolder[] = [];
        let hasAnySuccess = false;

        const fetchPromises: Promise<void>[] = [];

        if (hasGmailIntegration) {
          fetchPromises.push(
            getUserLabels()
              .then((labels) => {
                results.push(
                  ...labels.map((label) => ({
                    ...label,
                    platform: MessagingPlatform.GMAIL,
                  })),
                );
                hasAnySuccess = true;
              })
              .catch((error) => {
                log.error('Error getting Gmail labels:', error);
              }),
          );
        }

        if (hasOutlookIntegration) {
          fetchPromises.push(
            getUserFolders()
              .then((folders) => {
                const ensuredEmailAddresses = folders.map(ensureEmailAddress);
                results.push(
                  ...ensuredEmailAddresses.map((folder) => ({
                    ...folder,
                    platform: MessagingPlatform.OUTLOOK,
                  })),
                );
                hasAnySuccess = true;
              })
              .catch((error) => {
                log.error('Error getting Outlook folders:', error);
              }),
          );
        }

        await Promise.all(fetchPromises);

        if (hasAnySuccess) {
          setInboxLabels(results);
          markLabelsAsLoaded();
        }
      } catch (error) {
        log.error('Error getting labels/folders:', error);
      } finally {
        setLoadingFetchLabels(false);
      }
    },
    [setInboxLabels, setLoadingFetchLabels],
  );

  return {
    handleFetchLabels,
  };
};
